import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useCheckout } from "@/hooks/billing/useCheckout";
import { usePaymentSelectionStore } from "@/stores/paymentSelection/store";
import type { BillingCycle, PlanType } from "@/stores/paymentSelection/types";
import { PLANS, PLAN_RESOURCE_SUMMARY } from "../constants";

interface PlanGridProps {
  onboardingProjectId?: string;
  /**
   * Verb phrase describing what the user just tried to do
   * ("run this scan", "add another brand"). Used by `guardAction`-style
   * call sites where we want the headline to read like a sentence:
   * "To {trigger}, upgrade to Growth".
   */
  trigger?: string;
  /**
   * Noun phrase naming the feature or limit the user hit
   * ("Content Generation", "more than 100 prompts per project"). Used
   * by feature-gate call sites where the user is *blocked*, not
   * choosing — produces "Upgrade to unlock {feature}".
   *
   * Mutually exclusive with `trigger` in practice; if both are set,
   * `feature` wins because feature-gate framing is more specific.
   */
  feature?: string;
  /**
   * Display name of the user's current plan ("Starter", "Free", etc.).
   * When set, the description swaps to "You're on {currentPlan}. Upgrade
   * to {Recommended}." so the user understands why they're seeing the
   * surface.
   */
  currentPlan?: string;
  /** Org to subscribe; omitted = the user's personal org */
  orgSlug?: string;
}

export function PlanGrid({
  onboardingProjectId,
  trigger,
  feature,
  currentPlan,
  orgSlug,
}: PlanGridProps = {}) {
  const { handleCheckout } = useCheckout();
  const selectedBillingCycle = usePaymentSelectionStore(
    (s) => s.selectedBillingCycle,
  );
  const setSelectedPlan = usePaymentSelectionStore((s) => s.setSelectedPlan);
  const setSelectedBillingCycle = usePaymentSelectionStore(
    (s) => s.setSelectedBillingCycle,
  );

  const cycle = selectedBillingCycle ?? "annual";
  const recommended = PLANS.find((p) => p.type === "pro") ?? PLANS[0];
  const alternative = PLANS.find((p) => p.type !== recommended.type);

  const recommendedMonthly =
    cycle === "annual"
      ? Math.round(recommended.yearlyPrice / 12)
      : recommended.monthlyPrice;
  const recommendedAnnualSavings =
    recommended.monthlyPrice * 12 - recommended.yearlyPrice;
  const alternativeMonthly = alternative
    ? cycle === "annual"
      ? Math.round(alternative.yearlyPrice / 12)
      : alternative.monthlyPrice
    : 0;

  const eyebrow = onboardingProjectId ? "Free trial" : "Upgrade required";
  const headline = onboardingProjectId
    ? "Start your 14-day free trial"
    : feature
      ? `Upgrade to unlock ${feature}`
      : trigger
        ? `To ${trigger}, upgrade to ${recommended.name}`
        : `Continue with ${recommended.name}`;
  const description =
    currentPlan && !onboardingProjectId
      ? `You're on ${currentPlan}. Upgrade to ${recommended.name}.`
      : `${recommended.description}.`;

  const commit = (planType: string) => {
    const plan = planType as PlanType;
    setSelectedPlan(plan);
    setSelectedBillingCycle(cycle);
    const extras = {
      ...(onboardingProjectId
        ? { context: "onboarding", project_id: onboardingProjectId }
        : {}),
      ...(orgSlug ? { org_slug: orgSlug } : {}),
    };
    handleCheckout(
      plan,
      cycle,
      Object.keys(extras).length > 0 ? extras : undefined,
    );
  };

  return (
    <div className="space-y-5">
      <div className="space-y-2">
        <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
          {eyebrow}
        </span>
        <h2 className="text-lg font-medium">{headline}</h2>
        <p className="text-sm text-muted-foreground">{description}</p>
      </div>

      <Tabs
        value={cycle}
        onValueChange={(v) => setSelectedBillingCycle(v as BillingCycle)}
      >
        <TabsList>
          <TabsTrigger value="monthly">Monthly</TabsTrigger>
          <TabsTrigger value="annual">Annual</TabsTrigger>
        </TabsList>
      </Tabs>

      <div className="space-y-4 rounded-xl border-[0.5px] border-border bg-card p-5">
        <div className="space-y-3">
          <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
            Recommended
          </span>
          <div className="flex items-baseline justify-between gap-6">
            <div className="text-base font-medium">{recommended.name}</div>
            <div>
              <span className="text-3xl font-medium tabular-nums text-foreground">
                ${recommendedMonthly}
              </span>
              <span className="text-sm text-muted-foreground"> /mo</span>
            </div>
          </div>
          <p className="text-sm text-muted-foreground">
            {recommended.description} ·{" "}
            {cycle === "annual" ? "billed annually" : "billed monthly"} · cancel
            anytime
          </p>
        </div>

        <div className="border-t border-border" />

        <dl className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
          {PLAN_RESOURCE_SUMMARY[
            recommended.type as keyof typeof PLAN_RESOURCE_SUMMARY
          ]?.map((row) => (
            <div key={row.key} className="space-y-0.5">
              <dt className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                {row.label}
              </dt>
              <dd className="text-foreground tabular-nums">{row.value}</dd>
            </div>
          ))}
        </dl>
      </div>

      <div className="space-y-2">
        <Button onClick={() => commit(recommended.type)} className="w-full">
          {onboardingProjectId
            ? "Start 14-day free trial"
            : `Continue with ${recommended.name}`}
        </Button>
        <p className="text-center text-xs text-muted-foreground">
          {onboardingProjectId
            ? "No charge for 14 days. Cancel anytime."
            : "Cancel anytime. Prorated on plan changes."}
        </p>
      </div>

      {cycle === "monthly" && (
        <div className="flex items-center justify-between gap-3 rounded-lg bg-muted/60 px-4 py-3">
          <p className="text-sm text-muted-foreground">
            <span className="font-medium text-foreground">
              Save ${recommendedAnnualSavings.toLocaleString()}/year
            </span>{" "}
            with annual billing
          </p>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setSelectedBillingCycle("annual")}
          >
            Switch to annual
          </Button>
        </div>
      )}

      {alternative && (
        <div className="text-center text-sm">
          <span className="text-muted-foreground">Need less? </span>
          <button
            type="button"
            onClick={() => commit(alternative.type)}
            className="text-foreground underline underline-offset-2 hover:no-underline"
          >
            Start with {alternative.name} — ${alternativeMonthly}/mo
          </button>
        </div>
      )}
    </div>
  );
}
